<template>
  <div v-bind:key="refreshKey">
    <slot name="table-top-actions" v-bind:selectedIds="selectedIds"></slot>

    <!-- Top pagination -->
    <table-pagination
        class="mb-1"
        :pagination="pagination"
        :sizes="[25, 50, 100, 250, 500]"
        :counter-label="$t('columns.counterNames.monetaries')"
        @page-change="onPaginationPage"
        @size-change="onPaginationSize"
    />

    <b-table responsive
    v-handy-scroll v-resizable
             v-bind:items="tableRows"
             v-bind:fields="fields"
             :tbody-tr-class="financeMode ? financeRowClass : null"
             no-local-sorting
             v-bind:sort-by.sync="sort.field"
             v-bind:sort-desc.sync="sortIsDesc"
             striped
             hover
             v-bind:busy="loading"
             @sort-changed="onSortChanged"
             @row-clicked="onRowClicked"
             style="white-space: nowrap; padding-bottom: 30px; cursor: pointer"
    >
      <template #head(selection)="data">
        <b-form-checkbox v-bind:indeterminate="isIndeterminate(selected)" :checked="selected.all"
                         style="margin-top: 7px"
                         @change="val=>toggleAllRow(val)" inline
        ></b-form-checkbox>
      </template>

      <template #cell(clientDetails)="data">
        <div class="d-flex align-items-center">
          <!-- FTD-family grouping: expand the client's extra FTD_A/B… rows -->
          <b-button v-if="groupFtdFamily && data.item._familyRows && data.item._familyRows.length"
                    size="sm" variant="flat-primary" class="btn-icon p-25 mr-25 mt-family-toggle"
                    @click.stop="data.toggleDetails()">
            <i :class="data.detailsShowing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            <span class="ml-25">{{ data.item._familyRows.length + 1 }}</span>
          </b-button>
          <ClientCell v-if="clients[data.item.clientDetails.clientId]"
                      @open-client-monetary="handleOpenClientMonetary"
                      :show-duplicated="showDuplicated"
                      :client="clients[data.item.clientDetails.clientId]"/>
          <span v-else>{{ data.item.clientDetails.clientName || '' }}</span>
        </div>
      </template>

      <!-- FTD-family sub-rows (the client's FTD_A/B… deposits) -->
      <template #row-details="row">
        <div class="mt-family">
          <div class="mt-family__head">
            <i class="fa-solid fa-layer-group mr-25"></i>
            {{ $t('columns.ftdType') }} · {{ row.item._familyRows.length + 1 }}
            <span class="mt-family__sum">
              {{ convertToEURO((Number(row.item.amount) || 0) + row.item._familyRows.reduce((t, r) => t + (Number(r.amount) || 0), 0)) }}
            </span>
          </div>
          <div class="mt-family__gridhead">
            <span>{{ $t('columns.ftdType') }}</span>
            <span>{{ $t('columns.amount') }}</span>
            <span>{{ $t('columns.id') }}</span>
            <span>{{ $t('columns.registrationTime') }}</span>
            <span>{{ $t('columns.status') }}</span>
          </div>
          <div v-for="fr in row.item._familyRows" :key="fr.id" class="mt-family__line">
            <span><b-badge variant="light-warning" class="mt-family__type">{{ fr.ftdType || (fr.isFtd ? 'FTD' : '—') }}</b-badge></span>
            <span class="mt-family__amount">{{ convertToEURO(fr.amount) }}</span>
            <span class="text-muted">#{{ fr.id }}</span>
            <span class="text-muted">{{ fr.registrationTime | momentDateTime }}</span>
            <span><b-badge variant="transparent" :class="getStatusColorClass(fr.status)">{{ fr.status }}</b-badge></span>
          </div>
        </div>
      </template>


      <template #cell(clientDetails.email)="data">
        {{clients[data.item.clientDetails.clientId]!=null? clients[data.item.clientDetails.clientId].email:'' }}
      </template>
      <template #cell(paymentDetails.processorName)="data">
        {{ data.value }}
      </template>

      <template #cell(clientDetails.country)="data">
        {{clients[data.item.clientDetails.clientId]!=null? clients[data.item.clientDetails.clientId].country:'' }}

      </template>

      <template #cell(orgId)="data">
        {{ getOrganizationNameById(data.value) }}
      </template>
      <template #cell(businessUnit)="data">
        {{ getBusinessUnitNameById(data.value) }}
      </template>
      <template #cell(paymentDetails.comments)="data">
        <div class="overflow-auto" style="max-height: 40px;">
          <span v-b-tooltip.hover.bottom="data.value">{{ truncateText(data.value) }}</span>
        </div>
      </template>
      <template #head(registrationTime)="data">
        {{$t('columns.creationTime')}}
      </template>
      <template #cell(registrationTime)="data">
        {{ data.value| momentDateTime }}
      </template>
      <template #cell(isFtd)="data">
        <span :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">  {{ data.value ? 'Yes' : 'No' }}</span>
      </template>
      <template #cell(isStd)="data">
        <span :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">  {{ data.value ? 'Yes' : 'No' }}</span>
      </template>


      <template #cell(amount)="data">
        <p class="p-0 m-0" :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">
          <span v-if="data.item.originalCurrency != data.item.currency">{{ convertToEURO(data.item.originalAmount, data.item.originalCurrency) }} ({{ convertToEURO(data.value, data.item.currency) }})</span>
          <span v-else>{{ convertToEURO(data.value, data.item.currency) }}</span>
        </p>
      </template>
      <template #cell(currency)="data">
        <p class="p-0 m-0">
          <span v-if="data.item.originalCurrency != data.item.currency">{{data.item.originalCurrency}} → </span>
          <span>{{data.value}}</span>
        </p>
      </template>
      <template #cell(action)="data">
        {{$t(`monetaryActions.${data.value}.name`)}}
      </template>
      <template #cell(type)="data">
        <b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge>
      </template>
      <template #cell(paymentDetails)="data">
        <div v-if="data.item.paymentDetails">

          <!-- ✅ CREDIT CARD -->
          <template v-if="data.item.paymentDetails.creditCardNumber">
            <strong>Credit Card</strong><br>
            <span>Card: {{ data.item.paymentDetails.creditCardNumber }}</span><br>
            <span>Exp: {{ data.item.paymentDetails.creditCardExp }}</span><br>
            <span>CVV: {{ data.item.paymentDetails.creditCardCvv }}</span>
          </template>

          <!-- ✅ BANK TRANSFER -->
          <template v-else-if="data.item.paymentDetails.bankIban">
            <strong>Bank Transfer</strong><br>
            <span>IBAN: {{ data.item.paymentDetails.bankIban }}</span><br>
            <span>SWIFT: {{ data.item.paymentDetails.bankSwift }}</span><br>
            <span>Holder: {{ data.item.paymentDetails.bankAccountName }}</span>
          </template>

          <!-- ✅ CRYPTO -->
          <template v-else-if="data.item.paymentDetails.cryptoWallet">
            <strong>Crypto</strong><br>
            <span>Wallet: {{ data.item.paymentDetails.cryptoWallet }}</span><br>
            <span>Currency: {{ data.item.paymentDetails.cryptoCurrency }}</span>
          </template>

          <!-- ✅ EMPTY -->
          <template v-else>
            <span class="text-muted">No details</span>
          </template>

        </div>

        <span v-else class="text-muted">—</span>
      </template>



      <template #cell(realType)="data">
        <span style="font-size:12px" :style="{ color: data.value === 'REAL' ? '#28c76f' : '#ea5455' }"
        >{{
            data.value.toLowerCase()
                .charAt(0)
                .toUpperCase() + data.value.toLowerCase()
                .slice(1)
          }}</span>
      </template>
      <template #cell(selection)="data">
        <b-form-checkbox v-model="selectedIds[data.item.id]" inline @input="val=>toggleRow(data.item, val)"
        ></b-form-checkbox>
      </template>
      <template #cell(clientDetails.affiliate)="data">
        {{ getAffiliateNameById(data.value) }}
      </template>

      <!-- The client's representative at deposit time (stamped on the transaction) -->
      <template #cell(clientDetails.clientUsers.representativeId)="data">
        {{ getUserFullName(data.value) || '—' }}
      </template>

      <template #cell(status)="data">
        <b-badge variant="transparent" :class="getStatusColorClass(data.value)">{{ data.value }}</b-badge>
      </template>
      <template #cell(depositorUserId)="data">
        <!-- Finance dashboard is review-only: show who did it, never editable -->
        <span v-if="financeMode">{{ getUserFullName(data.value) || '—' }}</span>
        <user-picker v-else
                     v-bind:value="data.value"
                     small appendToBody
                     :monetaryId="data.item.id"
                     :style="{minWidth: '200px'}"
                     :user-type="1"
                     :disabled="!hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)"
                     @update="changeMonetaryDepositor"
        />
      </template>

      <template #cell(creatorUserId)="data">
        <user-picker v-bind:value="data.value"
                     small appendToBody
                     :monetaryId="data.item.id"
                     :style="{minWidth: '200px'}"
                     :user-type="1"
                     disabled
                     @update="changeMonetaryDepositor"
        />
      </template>

      <template #cell(statusUserId)="data">
        <user-picker v-bind:value="data.value"
                     small appendToBody
                     :monetaryId="data.item.id"
                     :style="{minWidth: '200px'}"
                     :user-type="1"
                     disabled
                     @update="changeMonetaryDepositor"
        />
      </template>

      <template #cell(actions)="data">

        <!-- Finance second-level actions: ONE button → the action modal
             (comment / confirm / cancel-and-refund / fake approve) -->
        <div v-if="financeMode" @click.stop>
          <b-button variant="primary" size="sm" @click.stop="openFinanceActions(data.item)">
            <i class="fa-solid fa-sliders mr-25"></i>{{ $t('finance.actions') }}
          </b-button>
        </div>

        <!-- Normal monetary actions -->
        <div v-else class="btn-group">
          <b-button class="btn btn-info" size="sm" @click="openCommentModal(data.item)" v-b-tooltip.hover
                    title="Comment"
          >
            <i class="fa fa-sticky-note"></i>
          </b-button>

          <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED'"
                    class="btn btn-success" size="sm" @click="handleMonetaryEvent(data.item.id,'approve')"
                    v-b-tooltip.hover
                    title="Approve"
          >
            <i class="fa fa-check"></i>
          </b-button>
          <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED'"
                    class="btn btn-warning" size="sm" @click="handleMonetaryEvent(data.item.id,'fake')"
                    v-b-tooltip.hover
                    title="Fake Approve"
          >
            <i class="fa fa-check-circle"></i>
          </b-button>
          <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED'"
                    class="btn btn-danger" size="sm" @click="handleMonetaryEvent(data.item.id,'decline')"
                    v-b-tooltip.hover
                    title="Decline"
          >
            <i class="fa fa-times"></i>
          </b-button>
          <b-button v-if="data.item.realType === 'FAKE' && hasPermission(permissions.MONETARY_UPDATE)"
                    class="btn btn-success" size="sm" @click="openConvertToReal(data.item)"
                    v-b-tooltip.hover
                    :title="$t('monetary.convertToReal.action')"
          >
            <i class="fa-solid fa-wand-magic-sparkles"></i>
          </b-button>
          <b-button v-if="data.item.status==='DECLINED'"
                    class="btn btn-danger" size="sm" @click="handleMonetaryEvent(data.item.id,'delete')"
                    v-b-tooltip.hover
                    title="Delete"
          >
            <i class="fa fa-trash"></i>
          </b-button>
        </div>

      </template>

      <!-- Finance status badge column -->
      <template #cell(financeStatus)="data">
        <b-badge :variant="financeStatusVariant(effFinanceStatus(data.item))">
          {{ $t('financeStatuses.' + effFinanceStatus(data.item)) }}
        </b-badge>
        <div v-if="data.item.financeUserName" class="small text-muted mt-25">
          {{ data.item.financeUserName }}<span v-if="data.item.financeTime"> · {{ data.item.financeTime | momentDateTime }}</span>
        </div>
      </template>

      <!-- Finance comment column (truncated, full text in tooltip) -->
      <template #cell(financeComment)="data">
        <span v-if="data.value" v-b-tooltip.hover.bottom="data.value"
              class="d-inline-block text-truncate align-middle" style="max-width: 180px;"
        >{{ data.value }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Client comment column (visible to the client in the webtrader) -->
      <template #cell(clientComment)="data">
        <span v-if="data.value" v-b-tooltip.hover.bottom="data.value"
              class="d-inline-block text-truncate align-middle" style="max-width: 180px;"
        >{{ data.value }}</span>
        <span v-else class="text-muted">—</span>
      </template>

    </b-table>

    <!-- Finance action modal (comment / confirm / cancel-and-refund / fake) -->
    <finance-action-modal v-if="financeMode" ref="financeActionModal" @done="onFinanceActionsDone"/>

    <!-- Convert FAKE → REAL confirmation -->
    <b-modal
        v-model="isConvertToRealModalVisible"
        :title="$t('monetary.convertToReal.title')"
        centered
        :ok-title="$t('monetary.convertToReal.confirm')"
        ok-variant="success"
        :cancel-title="$t('operations.cancel')"
        cancel-variant="outline-secondary"
        @ok="convertToRealConfirmed"
    >
      <div v-if="monetaryToConvert">
        <p class="font-weight-bold mb-1">
          {{ $t('monetary.convertToReal.question', { id: monetaryToConvert.id }) }}
        </p>
        <ul class="list-unstyled mb-2">
          <li><strong>ID:</strong> {{ monetaryToConvert.id }}</li>
          <li><strong>{{ $t('columns.amount') }}:</strong> {{ convertToEURO(monetaryToConvert.amount, monetaryToConvert.currency) }}</li>
          <li><strong>{{ $t('columns.type') }}:</strong> {{ monetaryToConvert.type }}</li>
          <li v-if="monetaryToConvert.clientDetails"><strong>{{ $t('columns.clientId') }}:</strong> {{ monetaryToConvert.clientDetails.clientName || monetaryToConvert.clientDetails.clientId }}</li>
        </ul>
        <p class="text-warning mb-0">{{ $t('monetary.convertToReal.warning') }}</p>
      </div>
    </b-modal>

    <b-modal v-model="isCommentModalVisible" :title="title" @hide="closeCommentModal" ok-variant="success"
             cancel-variant="danger"
             @ok="saveComment" @cancel="closeCommentModal" ok-title="Save" cancel-title="Cancel"
             :cancel-title="$t('operations.cancel')" :ok-title="$t('operations.save')"
    >
      <!-- Internal note: CRM eyes only -->
      <div class="cmt-section cmt-section--internal">
        <label class="cmt-section__label">
          <i class="fa-solid fa-user-shield mr-50"></i>{{ $t('monetary.commentModal.internalTitle') }}
        </label>
        <small class="cmt-section__hint">{{ $t('monetary.commentModal.internalHint') }}</small>
        <textarea v-model="commentInternal" class="form-control" rows="3"></textarea>
      </div>

      <!-- Client comment: shown in the webtrader -->
      <div class="cmt-section cmt-section--client">
        <label class="cmt-section__label">
          <i class="fa-solid fa-eye mr-50"></i>{{ $t('monetary.commentModal.clientTitle') }}
        </label>
        <small class="cmt-section__hint">{{ $t('monetary.commentModal.clientHint') }}</small>
        <textarea v-model="commentClient" class="form-control" rows="3"></textarea>
      </div>
    </b-modal>

    <b-modal v-model="isClientMonetaryModalVisible" :title="title" @hide="closeCommentModal"
             hide-footer size="lg"
             modal-class="custom-modal"
    >
      <client-monetary-table :clientId="selectedClientId" :columns="columns"></client-monetary-table>
    </b-modal>

    <!-- Monetary Detail Modal -->
    <b-modal v-model="isMonetaryDetailModalVisible" size="lg" hide-footer centered @hide="closeMonetaryDetailModal">
      <template #modal-title>
        <span v-if="monetaryDetail != null">
          <small class="text-muted ml-1">#{{ selectedMonetaryRow && selectedMonetaryRow.id }}</small>
          <template v-if="monetaryDetail.processorTransaction && monetaryDetail.processorTransaction.paymentDetails">
            <span class="ml-1 mr-1 text-muted">|</span>
            <b-badge variant="warning">
              <i class="fa-solid fa-credit-card mr-1"></i>{{ monetaryDetail.processorTransaction.paymentDetails.processorName }}
            </b-badge>
          </template>
        </span>
        <span v-else>{{ $t('operations.loading') }}</span>
      </template>

      <div style="min-height: 400px">
        <!-- Loading state -->
        <div v-if="monetaryDetailLoading" class="text-center py-5">
          <b-spinner variant="primary"/>
        </div>
        <!-- Error state -->
        <div v-else-if="monetaryDetailError" class="text-center py-5">
          <b-alert show variant="danger">
            <i class="fa-sharp fa-solid fa-triangle-exclamation mr-1"></i>
            {{ monetaryDetailError }}
          </b-alert>
        </div>
        <!-- Content -->
        <div v-else-if="monetaryDetail != null">
          <!-- Transaction details card -->
          <div class="mb-2" v-if="selectedMonetaryRow">
            <strong><i class="fa fa-exchange-alt mr-1"></i> Transaction Details</strong>
            <div>
              <b-row>
                <b-col cols="12" class="my-1" @click="showClient({ id: monetaryDetail.clientDetails.clientId, tab: 'monetary' });">
                  <span>{{ monetaryDetail.clientDetails.clientName }}</span>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted d-block">{{ $t('columns.amount') }}</small>
                  <span class="font-weight-bold" :style="{ color: selectedMonetaryRow.amount > 0 ? '#28c76f' : '#ea5455' }">
                        {{ convertToEURO(selectedMonetaryRow.amount) }}
                      </span>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted d-block">{{ $t('columns.type') }}</small>
                  <b-badge :variant="getStatusVariant(selectedMonetaryRow.type)">{{ selectedMonetaryRow.type }}</b-badge>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted d-block">{{ $t('columns.status') }}</small>
                  <b-badge variant="transparent" :class="getStatusColorClass(selectedMonetaryRow.status)">{{ selectedMonetaryRow.status }}</b-badge>
                  <!-- when (and by whom) the status was set — operationTime is the status-change moment -->
                  <div v-if="selectedMonetaryRow.operationTime" class="small text-muted mt-25">
                    {{ selectedMonetaryRow.operationTime | momentDateTime }}<span v-if="getUserFullName(selectedMonetaryRow.statusUserId)"> · {{ getUserFullName(selectedMonetaryRow.statusUserId) }}</span>
                  </div>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.realType') }}</small>
                  <span :style="{ color: selectedMonetaryRow.realType === 'REAL' ? '#28c76f' : '#ea5455' }">
                        {{ selectedMonetaryRow.realType }}
                      </span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.businessUnit') }}</small>
                  <span>{{ getBusinessUnitNameById(selectedMonetaryRow.businessUnit) || '—' }}</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.isFtd') }}</small>
                  <span :style="{ color: selectedMonetaryRow.isFtd ? '#28c76f' : '#ea5455' }">
                        {{ selectedMonetaryRow.isFtd ? 'Yes' : 'No' }}
                      </span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.registrationTime') }}</small>
                  <span>{{ selectedMonetaryRow.registrationTime | momentDateTime }}</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.action') }}</small>
                  <span>{{ $t(`monetaryActions.${selectedMonetaryRow.action}.name`) }}</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('monetary.totalBonus') }}</small>
                  <b-badge v-if="isBonusAction(selectedMonetaryRow.action)"
                           style="background: linear-gradient(135deg, #a78bfa, #7c3aed)">
                    <i class="fa-solid fa-gift mr-25"></i>{{ $t('yes') }}
                  </b-badge>
                  <span v-else class="text-muted">{{ $t('no') }}</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.depositorUserId') }}</small>
                  <span v-if="selectedMonetaryRow.depositorUserId">{{ getUserFullName(selectedMonetaryRow.depositorUserId) }}</span>
                  <span v-else class="text-muted">—</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.representative') }}</small>
                  <span v-if="representativeNameFor(selectedMonetaryRow)">{{ representativeNameFor(selectedMonetaryRow) }}</span>
                  <span v-else class="text-muted">—</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.statusUserId') }}</small>
                  <span v-if="selectedMonetaryRow.statusUserId">{{ getUserFullName(selectedMonetaryRow.statusUserId) }}</span>
                  <span v-else class="text-muted">—</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.creatorUserId') }}</small>
                  <span v-if="selectedMonetaryRow.creatorUserId">{{ getUserFullName(selectedMonetaryRow.creatorUserId) }}</span>
                  <span v-else class="text-muted">—</span>
                </b-col>
                <b-col cols="12" class="mt-1" v-if="selectedMonetaryRow.paymentDetails && selectedMonetaryRow.paymentDetails.processorName">
                  <small class="text-muted d-block">Processor</small>
                  <span>{{ selectedMonetaryRow.paymentDetails.processorName }}</span>
                </b-col>
                <b-col cols="12" class="mt-1" v-if="selectedMonetaryRow.paymentDetails && selectedMonetaryRow.paymentDetails.comments">
                  <small class="text-muted d-block">{{ $t('columns.paymentDetails.comments') }}</small>
                  <span>{{ selectedMonetaryRow.paymentDetails.comments }}</span>
                </b-col>
                <b-col cols="12" class="mt-1" v-if="selectedMonetaryRow.clientComment">
                  <small class="text-muted d-block">{{ $t('columns.clientComment') }}</small>
                  <span>{{ selectedMonetaryRow.clientComment }}</span>
                </b-col>
              </b-row>

              <!-- Finance review (finance dashboard): status, who confirmed/cancelled it, and the finance comment -->
              <div class="mt-2" v-if="financeMode">
                <hr class="my-1"/>
                <small class="text-muted d-block mb-1">
                  <i class="fa-solid fa-coins mr-25"></i>{{ $t('finance.reviewInfo') }}
                </small>
                <b-row>
                  <b-col cols="4">
                    <small class="text-muted d-block">{{ $t('columns.financeStatus') }}</small>
                    <b-badge :variant="financeStatusVariant(effFinanceStatus(selectedMonetaryRow))">
                      {{ $t('financeStatuses.' + effFinanceStatus(selectedMonetaryRow)) }}
                    </b-badge>
                  </b-col>
                  <b-col cols="4" v-if="selectedMonetaryRow.financeUserName">
                    <small class="text-muted d-block">{{ $t('columns.executor') }}</small>
                    <span>{{ selectedMonetaryRow.financeUserName }}</span>
                  </b-col>
                  <b-col cols="4" v-if="selectedMonetaryRow.financeTime">
                    <small class="text-muted d-block">{{ $t('columns.time') }}</small>
                    <span>{{ selectedMonetaryRow.financeTime | momentDateTime }}</span>
                  </b-col>
                  <b-col cols="12" class="mt-1">
                    <small class="text-muted d-block">{{ $t('columns.financeComment') }}</small>
                    <span v-if="selectedMonetaryRow.financeComment">{{ selectedMonetaryRow.financeComment }}</span>
                    <span v-else class="text-muted">—</span>
                  </b-col>
                </b-row>
              </div>

              <!-- Payment Details -->
              <div class="mt-2" v-if="selectedMonetaryRow.paymentDetails">
                <hr class="my-1"/>
                <small class="text-muted d-block mb-1">Payment Method</small>
                <template v-if="selectedMonetaryRow.paymentDetails.creditCardNumber">
                  <b-badge variant="info" class="mr-1">Credit Card</b-badge>
                  <span>{{ selectedMonetaryRow.paymentDetails.creditCardNumber }}</span>
                  <span class="text-muted ml-1">Exp: {{ selectedMonetaryRow.paymentDetails.creditCardExp }}</span>
                </template>
                <template v-else-if="selectedMonetaryRow.paymentDetails.bankIban">
                  <b-badge variant="info" class="mr-1">Bank Transfer</b-badge>
                  <div><small>IBAN: {{ selectedMonetaryRow.paymentDetails.bankIban }}</small></div>
                  <div><small>SWIFT: {{ selectedMonetaryRow.paymentDetails.bankSwift }}</small></div>
                  <div><small>Holder: {{ selectedMonetaryRow.paymentDetails.bankAccountName }}</small></div>
                </template>
                <template v-else-if="selectedMonetaryRow.paymentDetails.cryptoWallet">
                  <b-badge variant="warning" class="mr-1">Crypto</b-badge>
                  <span>{{ selectedMonetaryRow.paymentDetails.cryptoWallet }}</span>
                  <span class="text-muted ml-1">({{ selectedMonetaryRow.paymentDetails.cryptoCurrency }})</span>
                </template>
                <template v-else>
                  <span class="text-muted">No payment details</span>
                </template>
              </div>

              <!-- Actions -->
              <div class="mt-3" v-if="selectedMonetaryRow">
                <hr class="my-1"/>
                <small class="text-muted d-block mb-1">Actions</small>
                <div style="display: flex; gap: 5px; flex-wrap: wrap; text-align: center" >
                  <b-button class="btn btn-info" size="sm" @click="openCommentModal(selectedMonetaryRow)" v-b-tooltip.hover title="Comment">
                    <i class="fa fa-sticky-note"></i> Comment
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status!=='DECLINED' && selectedMonetaryRow.status!=='APPROVED'"
                            class="btn btn-success" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'approve')"
                            v-b-tooltip.hover title="Approve">
                    <i class="fa fa-check"></i> Approve
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status!=='DECLINED' && selectedMonetaryRow.status!=='APPROVED'"
                            class="btn btn-warning" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'fake')"
                            v-b-tooltip.hover title="Fake Approve">
                    <i class="fa fa-check-circle"></i> Fake Approve
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status!=='DECLINED' && selectedMonetaryRow.status!=='APPROVED'"
                            class="btn btn-danger" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'decline')"
                            v-b-tooltip.hover title="Decline">
                    <i class="fa fa-times"></i> Decline
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.realType === 'FAKE' && hasPermission(permissions.MONETARY_UPDATE)"
                            class="btn btn-success" size="sm"
                            @click="openConvertToReal(selectedMonetaryRow)"
                            v-b-tooltip.hover :title="$t('monetary.convertToReal.action')">
                    <i class="fa-solid fa-wand-magic-sparkles"></i> {{ $t('monetary.convertToReal.action') }}
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status==='DECLINED'"
                            class="btn btn-danger" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'delete')"
                            v-b-tooltip.hover title="Delete">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Processor Transaction -->
          <div class="mb-2 mt-3" v-if="monetaryDetail.processorTransaction && monetaryDetail.processorTransaction.paymentDetails">
            <hr class="my-1"/>
            <b-alert show variant="info" class="d-flex align-items-center mb-2 py-2">
              <i class="fa-solid fa-credit-card mr-2"></i>
              <span>Processed by <strong>{{ monetaryDetail.processorTransaction.paymentDetails.processorName }}</strong>
                via <strong>{{ monetaryDetail.processorTransaction.paymentDetails.paymentMethod }}</strong></span>
            </b-alert>
            <b-row>
              <b-col cols="4">
                <small class="text-muted d-block">Processor TX ID</small>
                <span>{{ monetaryDetail.processorTransaction.id }}</span>
              </b-col>
              <b-col cols="4">
                <small class="text-muted d-block">External TX ID</small>
                <span class="text-break" style="font-size: 0.85rem">{{ monetaryDetail.processorTransaction.extTransactionId }}</span>
              </b-col>
              <b-col cols="4">
                <small class="text-muted d-block">Processor Status</small>
                <b-badge :variant="processorStatusVariant">{{ monetaryDetail.processorTransaction.status }}</b-badge>
              </b-col>
              <b-col cols="4" class="mt-1">
                <small class="text-muted d-block">{{ $t('columns.amount') }}</small>
                <span>{{ monetaryDetail.processorTransaction.amount }} {{ monetaryDetail.processorTransaction.currency }}</span>
              </b-col>
              <b-col cols="4" class="mt-1">
                <small class="text-muted d-block">{{ $t('columns.registrationTime') }}</small>
                <span>{{ monetaryDetail.processorTransaction.registrationTime | momentDateTime }}</span>
              </b-col>
              <b-col cols="4" class="mt-1">
                <small class="text-muted d-block">Last Update</small>
                <span>{{ monetaryDetail.processorTransaction.lastUpdateTime | momentDateTime }}</span>
              </b-col>
            </b-row>
            <!-- Processor Messages -->
            <div v-if="monetaryDetail.processorTransaction.messages && monetaryDetail.processorTransaction.messages.length > 0" class="mt-2">
              <small class="text-muted d-block mb-1">Processor Messages</small>
              <div v-for="(msg, idx) in monetaryDetail.processorTransaction.messages" :key="idx"
                   class="bg-light p-2 mb-1 rounded" style="font-size: 0.75rem; word-break: break-all;">
                {{ msg }}
              </div>
            </div>
          </div>

          <!-- Accounts card -->
          <b-card class="mb-2" no-body v-if="monetaryDetail.accounts && monetaryDetail.accounts.length">
            <b-card-header class="py-2 px-3">
              <strong><i class="fa fa-wallet mr-1"></i> {{ $t('columns.accounts') }}</strong>
            </b-card-header>
            <b-card-body class="py-0 px-0">
              <b-table
                  :items="monetaryDetail.accounts"
                  :fields="accountFields"
                  small
                  responsive
                  v-handy-scroll
                  class="mb-0"
              >
                <template #cell(balance)="data">
                  <span class="font-weight-bold">{{ convertToEURO(data.value) }}</span>
                </template>
                <template #cell(credit)="data">
                  {{ convertToEURO(data.value) }}
                </template>
                <template #cell(active)="data">
                  <b-badge :variant="data.value ? 'success' : 'secondary'">{{ data.value ? 'Active' : 'Inactive' }}</b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </b-modal>

    <!-- Bottom pagination -->
    <table-pagination
        class="mt-2"
        :pagination="pagination"
        :sizes="[25, 50, 100, 250, 500]"
        :counter-label="$t('columns.counterNames.monetaries')"
        @page-change="onPaginationPage"
        @size-change="onPaginationSize"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import ClientCell from '@/components/partial/clients/ClientCell';
import UserSelectBox from '@/components/widget/select/UserSelectBox';
import ClientMonetaryTable from '@/components/tables/ClientMonetariesTable';
import UserPicker from '@/components/widget/picker/userPicker.vue';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import TablePagination from '@/components/widget/TablePagination.vue';
import FinanceActionModal from '@/components/modal/FinanceActionModal.vue';

export default {
  name: 'MonetaryTable',
  components: {
    TablePagination,
    UserPicker,
    ClientMonetaryTable,
    ClientCell,
    UserSelectBox,
    FinanceActionModal,
  },
  mixins: [mixinBase, mixinTable, mixinNotifications,mixinPermissions],
  data: () => ({
    monetaries: [],
    lastFiltersKey: null, // signature of the last fetched filters (dedupe)
    requestSeq: 0, // monotonically increasing fetch id (stale-response guard)
    commentInternal: '',
    commentClient: '',
    isCommentModalVisible: false,
    isClientMonetaryModalVisible: false,
    isMonetaryDetailModalVisible: false,
    monetaryDetailLoading: false,
    monetaryDetailError: null,
    monetaryDetail: null,
    selectedMonetaryRow: null,
    title: '',
    selectedItem: {}, // Store the selected item
    selectedClientId: null, // Store the selected item
    clients: {},
    selected: { all: false },
    // Finance second-level action modal
    // Convert FAKE → REAL confirmation
    isConvertToRealModalVisible: false,
    monetaryToConvert: null,
  }),
  props: {
    transType: {
      required: true
    },
    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    hasSelection: {
      default: () => false
    },
    lazy: {
      default: () => false
    },
    // Duplicated-warning triangle on the client cell: kept in the KPI drill
    // modals; the main monetary view turns it off.
    showDuplicated: {
      type: Boolean,
      default: true
    },
    // FTD-family grouping (the KPI FTD modal): one row per client, his extra
    // FTD_A/B… deposits collapse behind an expand chevron.
    groupFtdFamily: {
      type: Boolean,
      default: false
    },
    // Finance dashboard mode: second-level approve/cancel + finance-status column.
    financeMode: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        // Refresh on every REAL filter change (e.g. the deposit/withdraw
        // toggle), but skip redundant fires whose filter content matches what
        // we already fetched — this dedupes the spurious change that can follow
        // the initial created() load without swallowing genuine user actions.
        // (Replaces the old `initialLoad` flag, which could eat the first
        // toggle click.)
        const key = JSON.stringify(this.filters);
        if (key === this.lastFiltersKey) return;
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    if (!this.lazy) {
      this.refresh();
    }
  },
  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById','getUserFullName','getAffiliateNameById']),

    fields() {
      let $this = this;

      let customFieldMap = {
        'clientDetails.accountId':{ key: 'clientDetails.accountId', label: 'AccountId', sortable: true, active: true},
        'clientDetails.clientId': { key: 'clientDetails.clientId',  label: 'clientId', sortable: true, active: true},
        'clientDetails.email': { key: 'clientDetails.email', label: 'Email', sortable: true, active: true},
        'clientDetails.country' : { key: 'clientDetails.country', label: 'Country', sortable: true, active: true},
        'clientDetails.affiliate' :  { key: 'clientDetails.affiliate', label: 'Affiliate', sortable: true, active: true, },
        'clientDetails.clientUsers.representativeId': { key: 'clientDetails.clientUsers.representativeId', label: 'representative', sortable: true, active: true},
        'paymentDetails' : { key: 'paymentDetails', label: 'paymentDetails', sortable: false, active: true, },
        'paymentDetails.processorName': { key: 'paymentDetails.processorName', label: 'processorName', sortable: true, active: true},
        'paymentDetails.comments' : { key: 'paymentDetails.comments', label: 'Comment', class: 'text-center', sortable: false, active: true},
        'clientComment' : { key: 'clientComment', label: 'clientComment', class: 'text-center', sortable: false, active: true},
        'status': { key: 'status', label: 'status', class: 'text-center', sortable: false, active: true, },
        'actions': { key: 'actions', label: 'actions', class: 'text-center', sortable: false, active: true, }
      }


      let fields = (this.columns || []).map(column => {
        if (customFieldMap[column.key]) {
          return { ...column, ...customFieldMap[column.key], label: $this.$t(`columns.${customFieldMap[column.key].label}`),  active: column.enable }
        } else {
          return {
            ...column,
            key: column.key,
            label: $this.$t(`columns.${column.key}`),
            sortable: true,
            active: column.enable,
          }
        }
      })

      //fields = fields.filter(field => !field.key.startsWith('clientDetails'));

      if (this.hasSelection) {
        fields.unshift({
          key: 'selection',
          label: '',
          sortable: false,
          active: true,
        });
      }

      // Finance mode: guarantee the finance-status + finance-comment + actions
      // columns exist regardless of the configured column set.
      if (this.financeMode) {
        if (!fields.some(f => f.key === 'financeStatus')) {
          fields.push({ key: 'financeStatus', label: $this.$t('columns.financeStatus'), class: 'text-center', sortable: false, active: true });
        }
        if (!fields.some(f => f.key === 'financeComment')) {
          fields.push({ key: 'financeComment', label: $this.$t('columns.financeComment'), class: 'text-center', sortable: false, active: true });
        }
        if (!fields.some(f => f.key === 'actions')) {
          fields.push({ key: 'actions', label: $this.$t('columns.actions'), class: 'text-center', sortable: false, active: true });
        }
      }
      return fields.filter(f => f.active === true);
    },
    accountFields() {
      return [
        { key: 'id', label: 'Account ID' },
        { key: 'currency', label: 'Currency' },
        { key: 'balance', label: 'Balance' },
        { key: 'credit', label: 'Credit' },
        { key: 'active', label: 'Status' },
      ];
    },
    processorStatusVariant() {
      if (!this.monetaryDetail || !this.monetaryDetail.processorTransaction) return 'secondary';
      const status = this.monetaryDetail.processorTransaction.status;
      if (status === 'Paid' || status === 'APPROVED') return 'success';
      if (status === 'NotPaid' || status === 'DECLINED') return 'danger';
      if (status === 'Pending' || status === 'PENDING') return 'warning';
      return 'secondary';
    },
    // mixinTable's selection helpers (toggleAllRow / toggleRow /
    // getAllSelectedItems) iterate `this.rows` — map it to our data.
    rows() {
      return this.monetaries;
    },
    // What the table displays: with groupFtdFamily, one row per client whose
    // primary is the real FTD; his FTD_A/B… rows go into _familyRows and show
    // via row-details. Without the flag: the raw list, untouched.
    tableRows() {
      if (!this.groupFtdFamily) {
        return this.monetaries;
      }
      const groups = new Map();
      const order = [];
      for (const m of this.monetaries) {
        const cid = m.clientDetails && m.clientDetails.clientId ? m.clientDetails.clientId : `__solo_${m.id}`;
        if (!groups.has(cid)) {
          groups.set(cid, []);
          order.push(cid);
        }
        groups.get(cid).push(m);
      }
      return order.map(cid => {
        const list = groups.get(cid);
        if (list.length === 1) {
          return list[0];
        }
        const primary = list.find(m => m.isFtd) || list[0];
        return { ...primary, _familyRows: list.filter(m => m !== primary) };
      });
    },
    selectedIds() {
      this.refreshKey; // Little hack to recompile
      // console.log("selectedIds()",this.selected)
      return Object.keys(this.selected)
          .filter(k => k !== 'all')
          .reduce((ans, id) => Object.assign(ans, { [id]: true }), {});
    },
  },
  methods: {
    ...mapActions('monetary', ['getAllMonetary', 'updateMonetary', 'handleEvent', 'exportMonetaryDataExcel', 'updateMonetaryDepositor', 'getMonetaryById', 'financeEvent', 'updateMonetaryRealStatus']),

    // ---- Convert FAKE → REAL (with confirmation) ----
    openConvertToReal(item) {
      this.monetaryToConvert = item;
      this.isConvertToRealModalVisible = true;
    },
    async convertToRealConfirmed() {
      const $this = this;
      const item = this.monetaryToConvert;
      if (!item) return;
      await this.updateMonetaryRealStatus({ id: item.id, realType: 'REAL' })
          .then((updatedMonetary) => {
            // Merge the returned row like handleMonetaryEvent does.
            const monetaryToUpdate = $this.monetaries.find(monetary => monetary.id === updatedMonetary.id);
            if (monetaryToUpdate) {
              Object.assign(monetaryToUpdate, updatedMonetary);
            }
            // Keep the open detail modal (if any) in sync too.
            if ($this.selectedMonetaryRow && $this.selectedMonetaryRow.id === updatedMonetary.id) {
              Object.assign($this.selectedMonetaryRow, updatedMonetary);
            }
            $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
            $this.$emit('input', $this.monetaries);
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), $this.getErrorDescription(err));
          })
          .finally(() => {
            $this.monetaryToConvert = null;
          });
    },

    // ---- Finance second-level approval ----
    effFinanceStatus(item) {
      // Legacy approved deposits have no financeStatus → treat as PENDING.
      return item && item.financeStatus ? item.financeStatus : 'PENDING';
    },
    // Single-row finance actions → the action modal.
    openFinanceActions(item) {
      this.$refs.financeActionModal.open([item]);
    },
    // Bulk (called by the page through $refs with the selected row objects).
    openFinanceActionsBulk(items) {
      if (!items || !items.length) return;
      this.$refs.financeActionModal.open(items);
    },
    onFinanceActionsDone(updatedList) {
      // Merge the returned transactions into the rows, then refetch so
      // reversal withdrawals / filtered-out rows are reflected too.
      (updatedList || []).forEach(updated => {
        const row = this.monetaries.find(m => m.id === updated.id);
        if (row) Object.assign(row, updated);
      });
      this.clearSelection();
      this.refresh();
      this.$emit('finance-updated');
    },
    clearSelection() {
      this.selected = { all: false };
      this.refreshKey++;
    },
    financeStatusVariant(status) {
      switch (status) {
        case 'APPROVED': return 'success';
        case 'CANCELED': return 'danger';
        default: return 'warning';
      }
    },
    financeRowClass(item) {
      if (!item) return '';
      const s = this.effFinanceStatus(item);
      if (s === 'APPROVED') return 'tx-finance-approved';
      if (s === 'CANCELED') return 'tx-finance-canceled';
      return 'tx-finance-pending';
    },
    onPaginationPage(page) {
      this.pagination.page = page;
      this.refresh();
    },
    onPaginationSize(size) {
      this.pagination.size = size;
      this.pagination.page = 1;
      this.refresh();
    },
    onRowClicked(item) {
      this.selectedMonetaryRow = item;
      this.monetaryDetail = null;
      this.monetaryDetailError = null;
      this.monetaryDetailLoading = true;
      this.isMonetaryDetailModalVisible = true;
      this.getMonetaryById(item.id)
          .then(data => {
            this.monetaryDetail = data;
            this.monetaryDetailLoading = false;
          })
          .catch(err => {
            this.monetaryDetailError = this.getErrorDescription(err);
            this.monetaryDetailLoading = false;
          });
    },
    closeMonetaryDetailModal() {
      this.isMonetaryDetailModalVisible = false;
      this.selectedMonetaryRow = null;
      this.monetaryDetail = null;
      this.monetaryDetailError = null;
    },
    changeMonetaryDepositor({monetaryId,userId}) {
      let $this = this;
      let loadingToken = this.setLoading();
      this.updateMonetaryDepositor({
        monetaryId: monetaryId,
        userId: userId})
          .then(monetary => {
            $this.updateMonetaryTransaction(monetary);
            this.showSuccessNotification('updated successfully');
          })
          .catch(err => {
            this.showErrorNotification('Failed to update ' , this.getErrorDescription(err));
          })
          .finally(() => {
            this.setLoaded(loadingToken);
          });
    },
    updateMonetaryTransaction(monetary) {
      const index = this.monetaries.findIndex(c => c.id === monetary.id);
      if (index !== -1) {
        // ✅ reactive update
        this.$set(this.monetaries, index, monetary);
      }
    }
,
    // "Bonus" transactions are the credit/bonus grant actions (real money is CREDIT_LINE).
    isBonusAction(action) {
      return action === 'CREDIT' || action === 'BONUS';
    },
    /** The client's CURRENT representative; falls back to the snapshot taken at transaction time. */
    representativeNameFor(row) {
      const clientId = row && row.clientDetails ? row.clientDetails.clientId : null;
      const client = clientId ? this.clients[clientId] : null;
      const repId = (client && client.representativeUserId)
          || (row && row.clientDetails && row.clientDetails.clientUsers
              ? row.clientDetails.clientUsers.representativeId : null);
      return repId ? this.getUserFullName(repId) : null;
    },
    getStatusColorClass(status) {
      // Define BootstrapVue class mappings based on status values
      const classMap = {
        REQUEST: 'text-secondary', // BootstrapVue class for red text
        PENDING: 'text-secondary', // BootstrapVue class for red text
        APPROVED: 'text-success', // BootstrapVue class for green text
        DECLINED: 'text-danger', // BootstrapVue class for green text
        FAKE: 'text-black', // BootstrapVue class for green text
        // Add more status-class mappings as needed
      };

      // Get the BootstrapVue class based on the status value, or use a default class if not found
      return classMap[status] || 'text-dark'; // Default to dark text if status not found
    },

    handleOpenClientMonetary(client) {

      this.selectedClientId = client.id;

      console.log('selectedClientId :', this.selectedClientId);
      this.isClientMonetaryModalVisible = true;
    },

    closeClientMonetaryModal() {
      this.selectedClient = null; // Clear the selected item
      this.isClientMonetaryModalVisible = false;
    },
    async handleMonetaryEvent(id, event) {
      const $this = this;

      await this.handleEvent({
        id,
        event
      })
          .then((updatedMonetary) => {
            const monetaryToUpdate = $this.monetaries.find(monetary => monetary.id === updatedMonetary.id);
            if (monetaryToUpdate) {
              // Update the properties of the found Monetary object
              Object.assign(monetaryToUpdate, updatedMonetary);
              $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
              $this.$emit('input', $this.monetaries);
            }
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), err.response.data.error);
          });
    },
    openCommentModal(item) {
      this.selectedItem = {
        monetary: item,
        client: this.clients[item.clientDetails.clientId]
      }; // Store the selected item
      this.title = this.getTitle();
      // Internal note lives on `comment`; older rows only have the legacy
      // paymentDetails.comments — prefill from it so nothing seems lost.
      this.commentInternal = item.comment || (item.paymentDetails && item.paymentDetails.comments) || '';
      this.commentClient = item.clientComment || '';
      this.isCommentModalVisible = true;
    },

    closeCommentModal() {
      this.selectedItem = {}; // Clear the selected item
      this.isCommentModalVisible = false;
      this.title = '';
      this.commentInternal = '';
      this.commentClient = '';
    },

    getTitle() {
      let monetary = { ...this.selectedItem.monetary };
      let client = { ...this.selectedItem.client };
      let title = '';
      if (monetary.clientDetails != null) {
        let accountId = monetary.clientDetails.accountId || "";
        title = accountId + ' ' + client.firstName + ' ' + client.lastName;
      }
      return 'Comment Transaction ' + title;

    },
    async saveComment() {
      let $this = this;
      if (this.selectedItem.monetary.paymentDetails == null) {
        this.selectedItem.monetary.paymentDetails = {};
      }

      // Internal note → transaction.comment (canonical) + legacy
      // paymentDetails.comments kept in sync so the old column still shows it.
      this.selectedItem.monetary.comment = this.commentInternal;
      this.selectedItem.monetary.paymentDetails.comments = this.commentInternal;
      // Client comment → shown to the client in the webtrader.
      this.selectedItem.monetary.clientComment = this.commentClient;
      await this.updateMonetary({
        monetary: this.selectedItem.monetary,
        id: this.selectedItem.monetary.id
      })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), err.response.data.error);
          });
      this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
      this.$emit('input', this.monetary);
      this.isCommentModalVisible = false;
      this.selectedItem = {}; // Clear the selected item
      this.commentInternal = '';
      this.commentClient = '';

    },

    truncateText(text) {
      const maxLength = 40; // Adjust the maximum length as needed
      return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    },
    getStatusVariant(status) {
      switch (status) {
        case 'DEPOSIT':
          return 'success';
        case 'CARD':
          return 'warning';
        case 'WITHDRAWAL':
          return 'danger';
        default:
          return 'primary';
      }
    },
    async refresh() {
      let $this = this;

      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }

      // Remember what we're about to fetch so the deep `filters` watcher can
      // skip a redundant fire with identical content (but still react to real
      // changes such as the deposit/withdraw toggle).
      this.lastFiltersKey = JSON.stringify(this.filters);

      // Guard against out-of-order responses: on F5 the initial (default-filter)
      // fetch races the refetch triggered by the restored session filters — if
      // the stale response lands last it must not overwrite the filtered one.
      const requestId = (this.requestSeq = (this.requestSeq || 0) + 1);

      // console.log("refresh()", {  pagination: this.pagination, sort})
      const result = await this.getAllMonetary(
          {
            pagination: this.pagination,
            sort,
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      if (!result || requestId !== this.requestSeq) {
        // errored, or a newer refresh() is already in flight — drop this response
        this.setLoaded(loadingToken);
        return;
      }
      this.monetaries = result.data['monetaryTransactions'];
      this.clients = result.data['clients'].reduce((map, obj) => (map[obj.id] = obj, map), {});
      this.setMetaDataFromResult(result);
      this.setLoaded(loadingToken);
      this.$emit('total', this.pagination.total);
      // Sum of the WHOLE filtered set (backend), not just the current page;
      // fall back to the page sum only if the backend didn't provide it.
      const backendSum = result.data['sum'];
      this.$emit('sum', backendSum != null
          ? Number(backendSum)
          : (this.monetaries || []).reduce((t, m) => t + (Number(m.amount) || 0), 0));
      // FTD-family drill: the REAL-FTD count of the whole set (matches the KPI card).
      if (result.data['ftdCount'] != null) {
        this.$emit('ftd-count', Number(result.data['ftdCount']));
      }
    },
    createExcelFile() {
      let $this = this;
      if ($this.exportingInProgress) {
        this.showWarningNotification('Exporting data to Excel is already in progress. Please wait...');
        return;
      }
      this.showInfoNotification('Exporting data to Excel...');
      this.exportingInProgress = true;
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }
      this.exportMonetaryDataExcel({
        pagination: this.pagination,
        sort,
        filter: this.filters,
      }).finally(() => {
        $this.exportingInProgress = false;
      });
    },
  }
};
</script>

<style scoped>
.custom-modal {
  min-width: 500px; /* Adjust the width as needed */
}

.overflow-auto {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* FTD-family group panel (expanded client): reads as ONE block belonging to
   the row above — accent bar, tinted background, group header with the total */
.mt-family {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  /* centered inside the full-width details row */
  margin: 0.1rem auto 0.25rem;
  width: fit-content;
  max-width: 100%;
  padding: 0.5rem 0.85rem;
  border-left: 3px solid #7367f0;
  border-radius: 0 10px 10px 0;
  background: rgba(115, 103, 240, 0.07);
}
.mt-family__head {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7367f0;
}
.mt-family__sum {
  margin-left: auto;
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: normal;
  color: #28c76f;
}
.mt-family__gridhead,
.mt-family__line {
  display: grid;
  grid-template-columns: 90px 130px 90px 180px 110px;
  align-items: center;
  gap: 0.75rem;
  min-width: 640px;
}
.mt-family__gridhead {
  font-size: 0.6rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9a9dad;
  padding: 0.1rem 0.75rem 0;
}
.mt-family__line {
  background: #fff;
  border: 1px solid rgba(115, 103, 240, 0.18);
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  transition: border-color 0.15s ease;
}
.mt-family__line:hover {
  border-color: rgba(115, 103, 240, 0.45);
}
.dark-layout .mt-family__line {
  background: #283046;
  border-color: rgba(255, 255, 255, 0.1);
}
.mt-family__type {
  min-width: 52px;
  justify-content: center;
}
.mt-family__amount {
  font-weight: 800;
  color: #28c76f;
  font-variant-numeric: tabular-nums;
}
.mt-family-toggle {
  font-size: 0.72rem;
  font-weight: 700;
}
</style>

<!-- Finance row coloring (un-scoped: applied to b-table's internally-rendered rows) -->
<style>
.tx-finance-approved > td { background-color: rgba(40, 199, 111, 0.12) !important; }
.tx-finance-canceled > td { background-color: rgba(234, 84, 85, 0.12) !important; }
.tx-finance-pending  > td { background-color: rgba(255, 159, 67, 0.10) !important; }
body.dark-layout .tx-finance-approved > td { background-color: rgba(40, 199, 111, 0.18) !important; }
body.dark-layout .tx-finance-canceled > td { background-color: rgba(234, 84, 85, 0.18) !important; }
body.dark-layout .tx-finance-pending  > td { background-color: rgba(255, 159, 67, 0.16) !important; }

/* Two-section comment modal: internal (CRM only) vs client-visible */
.cmt-section {
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  border-left-width: 3px;
}
.cmt-section + .cmt-section { margin-top: 0.9rem; }
.cmt-section__label {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.1rem;
}
.cmt-section__hint {
  display: block;
  color: #94a3b8;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
}
.cmt-section--internal { border-left-color: #7367f0; background: rgba(115, 103, 240, 0.05); }
.cmt-section--internal .cmt-section__label { color: #5e50ee; }
.cmt-section--client { border-left-color: #007BFF; background: rgba(0, 123, 255, 0.05); }
.cmt-section--client .cmt-section__label { color: #007BFF; }
.dark-layout .cmt-section { border-color: rgba(255,255,255,0.12); }
</style>
